/**
 * useNavAccess — the single source of truth for which nav items the current
 * user/account can see, and where a blocked navigation should land.
 *
 * Combines the user-level/permission filter with the per-account module filter
 * (Corp-App Module Settings) and the per-account Admin page levels. Used by the
 * Sidebar (rendering) and by the route guards — RequireModule, RequireAdminPage
 * — for the safe-landing redirect (restricted accounts' Dashboard `/` may
 * itself be module-disabled).
 */
import { useMemo } from 'react';
import {
  NAV_ITEMS,
  filterNavItemsByUserLevel,
  filterNavItemsByModuleAccess,
  filterNavItemsByAdminPageLevels,
  firstNavigableHref,
  type ModuleAccessMap,
  type NavItemDef,
} from '@/components/layout/navigation.config';
import { isModulesFresh, useAccountModules } from '@/hooks/useAccountModules';
import { useAdminPageLevels } from '@/hooks/useAdminPageLevels';
import { useAuthStore } from '@/store/authStore';

/** The per-account ModuleAccessMap ('/'+key → state) for nav filtering. */
export function useModuleAccessMap(): ModuleAccessMap {
  const query = useAccountModules();
  const accountModules = query.data;
  // Fail-closed `explicit` honors the same freshness rule as useModuleAccess:
  // the persisted authStore seed (or an errored refetch) must never keep an
  // opt-in nav entry visible after Corp-App revocation. Fail-open `enabled`/
  // `disabledSections` still come straight from the seed (anti-flicker).
  const fresh = isModulesFresh(query);
  return useMemo<ModuleAccessMap>(() => {
    const access: ModuleAccessMap = {};
    for (const m of accountModules?.modules ?? []) {
      // Include modules that are disabled, section-restricted, or explicitly
      // active — the last drives fail-closed `requiresModuleActive` entries.
      if (!m.enabled || m.disabledSections.length > 0 || m.explicit) {
        access[`/${m.key}`] = {
          enabled: m.enabled,
          explicit: fresh && m.explicit === true,
          disabledSections: m.disabledSections,
        };
      }
    }
    return access;
  }, [accountModules, fresh]);
}

/**
 * Whether this session is a corp super admin, for nav-visibility purposes.
 *
 * Deliberately no `?? userLevel >= 5` fallback, matching `useAuth`'s
 * `isSuperAdmin`: level 5 is grantable by a practice's own level-5 user, so
 * deriving from it would let a practice grant itself the corp-staff entries
 * this flag hides — Admin -> Import -> Zenoti writes into accounts the operator
 * is not a member of. A cached pre-flag session reads false until the
 * `/auth/me` refresh on Layout mount fills the flag in, which is the same
 * conservative direction `useAuth` already takes.
 *
 * Exported so the rule is testable on its own; the sidebar previously derived
 * this inline with the fallback, which made the level-5 case unprovable.
 */
export function deriveIsSuperAdmin(user: { isSuperAdmin?: boolean } | null | undefined): boolean {
  return user?.isSuperAdmin === true;
}

/**
 * The fully filtered sidebar tree for the current user + account: user gates,
 * then module access, then Admin page levels (a keyed Admin entry stays only
 * at Editable or Locked, from a fresh fetch). Because useSafeLandingPath reads
 * this tree, a blocked navigation never lands on a hidden Admin page.
 */
export function useFilteredNavItems(): NavItemDef[] {
  const user = useAuthStore((s) => s.user);
  const moduleAccess = useModuleAccessMap();
  const { levels: adminPageLevels } = useAdminPageLevels();

  const isSuperAdmin = deriveIsSuperAdmin(user);
  const isMasterAdmin = !!user?.isMasterAdmin;
  const userPermissions = useMemo(() => user?.permissions ?? [], [user?.permissions]);
  const hasQaNav = userPermissions.includes(1) || userPermissions.includes(24);
  const userLevel = user?.userLevel ?? 0;

  return useMemo(() => {
    let items = filterNavItemsByUserLevel(NAV_ITEMS, userLevel, isSuperAdmin, isMasterAdmin, userPermissions);
    if (!hasQaNav) {
      items = items.filter((i) => i.href !== '/qa');
    }
    return filterNavItemsByAdminPageLevels(filterNavItemsByModuleAccess(items, moduleAccess), adminPageLevels);
  }, [userLevel, isSuperAdmin, isMasterAdmin, hasQaNav, userPermissions, moduleAccess, adminPageLevels]);
}

/**
 * Where a blocked navigation should land. Prefer `/patients` whenever it is
 * visible — it is the core page every account keeps, and "first visible item"
 * is order-fragile (on staging the AI Assistant entry sits near the top and
 * made a blocked Add-Patient click land there). Fall back to the first
 * visible item, then `/patients` unconditionally.
 * Loop-proof by construction: the result is derived from *enabled* items.
 */
export function useSafeLandingPath(): string {
  const items = useFilteredNavItems();
  return useMemo(() => {
    const hasPatients = items.some((i) => i.href === '/patients');
    return hasPatients ? '/patients' : (firstNavigableHref(items) ?? '/patients');
  }, [items]);
}
